import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import { useState, useEffect, useRef } from "react";
import { Project, ProjectPhoto, User } from "types";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { showNotification } from "context/notification.service";
import PhotosMapModal from "@components/modals/PhotosMapModal";
import { useDataStore } from "@store/user_org";
import { isGuest as checkIsGuest } from "@utils/permissions";
import { reverseGeocode } from "@helpers/geocodingHelper";

// The photo's stored date (created_at). For in-app camera shots this is the
// moment it was added on-site; for gallery-picked images it's the upload time.
// created_at may be a Firestore Timestamp, a {seconds} object, or an ISO string.
function formatPhotoDate(photo?: ProjectPhoto): string | null {
  const raw: any = photo?.file?.created_at;
  if (!raw) return null;
  const d = raw.toDate
    ? raw.toDate()
    : raw.seconds
      ? new Date(raw.seconds * 1000)
      : new Date(raw);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const getVisiblePhotosFromProject = (project: Project, userData?: User) => {
  if (!project?.photos) return [];
  return project.photos.filter((photo) => !photo.hidden || userData);
};

interface PhotoModalProps {
  onClose: () => void;
  photo_id?: string;
  activeProject?: Project;
  setActiveProject?: (project: Project) => void;
  userData?: User;
}

export const PhotoModal = ({
  onClose,
  photo_id,
  activeProject,
  setActiveProject,
  userData,
}: PhotoModalProps) => {
  const storeUserData = useDataStore((state) => state.userData);
  const isGuest = checkIsGuest(userData || storeUserData);
  const [photo, setPhoto] = useState<ProjectPhoto | undefined>(
    activeProject?.photos.find((p) => p.id === photo_id),
  );
  const [mapOpen, setMapOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [fetchedSize, setFetchedSize] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [geoLabel, setGeoLabel] = useState<string | null>(null);
  const dragRef = useRef<{ x: number; y: number; px: number; py: number } | null>(
    null,
  );

  // Resolve a readable address for the photo's capture location, when known.
  useEffect(() => {
    setGeoLabel(null);
    const geo = photo?.geo_location;
    if (!geo || geo.latitude == null || geo.longitude == null) return;
    let cancelled = false;
    reverseGeocode(geo.latitude, geo.longitude).then((label) => {
      if (!cancelled) setGeoLabel(label);
    });
    return () => {
      cancelled = true;
    };
  }, [photo?.id]);

  useEffect(() => {
    if (!photo || photo.file.octets > 0) {
      setFetchedSize(0);
      return;
    }
    setFetchedSize(0);
    fetch(photo.file.url, { method: "HEAD" })
      .then((res) => {
        const len = res.headers.get("content-length");
        if (len) {
          const size = parseInt(len, 10);
          setFetchedSize(size);
          if (activeProject && setActiveProject) {
            setActiveProject({
              ...activeProject,
              photos: activeProject.photos.map((p) =>
                p.id === photo.id
                  ? { ...p, file: { ...p.file, octets: size } }
                  : p,
              ),
            });
          }
        }
      })
      .catch(() => {});
  }, [photo?.id]);

  function handleDelete() {
    if (!activeProject || !photo || !setActiveProject) return;
    setActiveProject({
      ...activeProject,
      photos: activeProject.photos.filter((p) => p.id !== photo.id),
    });
    onClose();
  }

  function handleToggleHidden() {
    if (!activeProject || !photo || !setActiveProject) return;
    const updatedPhoto = { ...photo, hidden: !photo.hidden };
    setPhoto(updatedPhoto);
    setActiveProject({
      ...activeProject,
      photos: activeProject.photos.map((p) =>
        p.id === photo.id ? updatedPhoto : p,
      ),
    });
  }

  function resetView() {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  }

  async function handleDownload() {
    if (!photo?.file?.url) return;
    try {
      const response = await fetch(photo.file.url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = photo.file.name || "photo.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(photo.file.url, "_blank");
    }
  }

  function goToNextPhoto() {
    if (!activeProject || !photo) return;
    const photos = getVisiblePhotosFromProject(activeProject, userData);
    const currentIndex = photos.findIndex((p) => p.id === photo.id);
    const nextIndex = (currentIndex + 1) % photos.length;
    setPhoto(photos[nextIndex]);
    resetView();
  }

  function goToPreviousPhoto() {
    if (!activeProject || !photo) return;
    const photos = getVisiblePhotosFromProject(activeProject, userData);
    const currentIndex = photos.findIndex((p) => p.id === photo.id);
    const previousIndex = (currentIndex - 1 + photos.length) % photos.length;
    setPhoto(photos[previousIndex]);
    resetView();
  }

  const isFacade =
    photo?.file.id === activeProject?.infos.general.photo_file?.id;

  function handleSetFacade() {
    if (!activeProject || !photo || !setActiveProject) return;
    setActiveProject({
      ...activeProject,
      infos: {
        ...activeProject.infos,
        general: {
          ...activeProject.infos.general,
          photo_file: photo.file,
        },
      },
    });
    showNotification({ type: "success", message: "Photo de façade définie" });
  }

  useEffect(() => {
    if (photo_id && activeProject) {
      const foundPhoto = activeProject.photos.find((p) => p.id === photo_id);
      if (foundPhoto) setPhoto(foundPhoto);
    }
  }, [photo_id]);

  useEffect(() => {
    if (!photo || !activeProject) return;
    const refreshed = activeProject.photos.find((p) => p.id === photo.id);
    if (refreshed && refreshed !== photo) setPhoto(refreshed);
  }, [activeProject]);

  useEffect(() => {
    if (zoom <= 1) setPan({ x: 0, y: 0 });
  }, [zoom]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goToPreviousPhoto();
      else if (e.key === "ArrowRight") goToNextPhoto();
      else if (e.key === "+" || e.key === "=")
        setZoom((z) => Math.min(5, +(z * 1.25).toFixed(2)));
      else if (e.key === "-")
        setZoom((z) => Math.max(0.25, +(z / 1.25).toFixed(2)));
      else if (e.key === "0") resetView();
      else if (e.key.toLowerCase() === "r") setRotation((r) => (r + 90) % 360);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!photo || !activeProject) return null;

  const photos = getVisiblePhotosFromProject(activeProject, userData);
  const currentIndex = photos.findIndex((p) => p.id === photo.id);

  const fileSize = photo.file.octets > 0 ? photo.file.octets : fetchedSize;
  const formattedSize =
    fileSize > 0
      ? fileSize < 1024 * 1024
        ? `${(fileSize / 1024).toFixed(0)} Ko`
        : `${(fileSize / (1024 * 1024)).toFixed(1)} Mo`
      : null;

  const lightCircle =
    "w-9 h-9 rounded-full bg-[#ececea] hover:bg-neutral-300 transition-colors grid place-items-center text-neutral-700";
  const darkCircle =
    "w-[42px] h-[42px] rounded-full bg-neutral-900 hover:bg-neutral-700 grid place-items-center shadow-[0_2px_6px_rgba(0,0,0,0.12)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.25)] hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150 ease-out";

  return (
    <BaseModal
      width={isFullscreen ? "max-w-6xl" : "max-w-[1040px]"}
      display={isFullscreen ? "fullscreen" : undefined}
      title="Photo"
      startIcon={<Icon1 name="image" size={20} />}
      onClose={onClose}
    >
      {confirmationOpen && (
        <ConfirmationModal
          onClose={() => setConfirmationOpen(false)}
          onConfirm={handleDelete}
          title="Supprimer la photo"
          message="Êtes-vous sûr de vouloir supprimer cette photo ?"
        />
      )}
      {mapOpen && (
        <PhotosMapModal
          project={activeProject}
          currentPhotoId={photo.id}
          onClose={() => setMapOpen(false)}
          onSelectPhoto={(id) => {
            const found = activeProject.photos.find((p) => p.id === id);
            if (found) setPhoto(found);
            setMapOpen(false);
          }}
        />
      )}

      <div className="flex flex-col bg-white">
        {/* Viewer */}
        <div className="px-6 pt-4 pb-1">
          <div
            className="relative w-full bg-[#f3f3f1] rounded-[10px] overflow-hidden"
            style={{
              height: isFullscreen
                ? "calc(100vh - 260px)"
                : "calc(90vh - 300px)",
              maxHeight: isFullscreen ? undefined : "560px",
              minHeight: "240px",
            }}
            onMouseMove={(e) => {
              if (!dragRef.current) return;
              setPan({
                x: dragRef.current.px + (e.clientX - dragRef.current.x),
                y: dragRef.current.py + (e.clientY - dragRef.current.y),
              });
            }}
            onMouseUp={() => (dragRef.current = null)}
            onMouseLeave={() => (dragRef.current = null)}
          >
            <img
              src={photo.file.url}
              alt=""
              draggable={false}
              className="w-full h-full select-none"
              style={{
                objectFit: "contain",
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                transformOrigin: "center center",
                transition: dragRef.current
                  ? "none"
                  : "transform 240ms cubic-bezier(.2,.7,.3,1)",
                cursor:
                  zoom > 1
                    ? dragRef.current
                      ? "grabbing"
                      : "grab"
                    : "default",
              }}
              onMouseDown={(e) => {
                if (zoom <= 1) return;
                e.preventDefault();
                dragRef.current = {
                  x: e.clientX,
                  y: e.clientY,
                  px: pan.x,
                  py: pan.y,
                };
              }}
              onWheel={(e) => {
                e.preventDefault();
                setZoom((z) =>
                  Math.min(5, Math.max(0.25, z + (e.deltaY < 0 ? 0.2 : -0.2))),
                );
              }}
            />
            {photo.hidden && (
              <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-700 bg-white/95 border border-neutral-300 rounded-full px-2.5 py-1 shadow-sm">
                <Icon1 name="invisible" size={12} />
                Masquée
              </div>
            )}
            {isFacade && (
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-white bg-blue-500 rounded-full px-2.5 py-1">
                <Icon1 name="image" size={12} color="white" />
                Façade
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="px-6 pt-3 pb-3 flex flex-col items-center gap-2.5">
          {/* File line */}
          <div className="inline-flex items-center gap-2 text-xs text-neutral-500">
            <span className="font-mono text-neutral-700">{photo.file.name}</span>
            {formattedSize && (
              <>
                <span className="text-neutral-400">—</span>
                <span>{formattedSize}</span>
              </>
            )}
          </div>

          {/* Date + location of capture */}
          {(formatPhotoDate(photo) ||
            photo.geo_location?.latitude != null) && (
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-neutral-600">
              {formatPhotoDate(photo) && (
                <span className="inline-flex items-center gap-1.5">
                  <Icon1 name="date" size={13} />
                  {formatPhotoDate(photo)}
                </span>
              )}
              {photo.geo_location?.latitude != null && (
                <span className="inline-flex items-center gap-1.5">
                  <Icon1 name="position" size={13} />
                  {geoLabel ||
                    `${photo.geo_location.latitude.toFixed(5)}, ${photo.geo_location.longitude.toFixed(5)}`}
                </span>
              )}
            </div>
          )}

          {/* Zoom row */}
          <div className="flex items-center gap-2.5">
            <button
              onClick={() =>
                setZoom((z) => Math.max(0.25, +(z / 1.25).toFixed(2)))
              }
              className={lightCircle}
              title="Dézoomer"
            >
              <Icon1 name="minus" size={16} />
            </button>
            <button
              onClick={resetView}
              className="min-w-14 h-9 px-3 rounded-full text-sm font-medium text-neutral-700 hover:bg-neutral-100 transition-colors tabular-nums"
              title="100%"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              onClick={() =>
                setZoom((z) => Math.min(5, +(z * 1.25).toFixed(2)))
              }
              className={lightCircle}
              title="Zoomer"
            >
              <Icon1 name="plus" size={16} />
            </button>
            <button
              onClick={() => setRotation((r) => (r + 90) % 360)}
              className={lightCircle}
              title="Pivoter"
            >
              <Icon1 name="sync" size={16} />
            </button>
          </div>

          {/* Action row */}
          <div className="flex items-center gap-3">
            <button
              onClick={goToPreviousPhoto}
              className={darkCircle}
              title="Photo précédente"
            >
              <Icon1 name="left" size={18} color="white" />
            </button>

            {!isGuest && (
              <button
                onClick={handleToggleHidden}
                className={darkCircle}
                title={photo.hidden ? "Afficher" : "Masquer"}
              >
                <Icon1
                  name={photo.hidden ? "invisible" : "eye"}
                  size={18}
                  color="white"
                />
              </button>
            )}

            <button
              onClick={() => setIsFullscreen((v) => !v)}
              className={darkCircle}
              title={isFullscreen ? "Réduire" : "Plein écran"}
            >
              <Icon1 name="full-screen" size={18} color="white" />
            </button>

            {!isGuest && (
              <button
                onClick={handleSetFacade}
                className={`w-[42px] h-[42px] rounded-full grid place-items-center shadow-[0_2px_6px_rgba(0,0,0,0.12)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.25)] hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150 ease-out ${
                  isFacade
                    ? "bg-blue-500 hover:bg-blue-400"
                    : "bg-neutral-900 hover:bg-neutral-700"
                }`}
                title="Définir comme photo de façade"
              >
                <Icon1 name="image" size={18} color="white" />
              </button>
            )}

            {!isGuest && (
              <button
                onClick={() => setMapOpen(true)}
                className={darkCircle}
                title="Voir sur la carte"
              >
                <Icon1 name="position" size={18} color="white" />
              </button>
            )}

            {!isGuest && (
              <button
                onClick={() => setConfirmationOpen(true)}
                className={darkCircle}
                title="Supprimer la photo"
              >
                <Icon1 name="delete" size={18} color="white" />
              </button>
            )}

            <button
              onClick={goToNextPhoto}
              className={darkCircle}
              title="Photo suivante"
            >
              <Icon1 name="right" size={18} color="white" />
            </button>
          </div>

        </div>
      </div>

      {/* Footer */}
      <div className="border-t-1 px-4 py-3 flex flex-row justify-between items-center gap-3">
        <div className="flex flex-row gap-2">
          <button
            onClick={handleDownload}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-[9px] border border-neutral-300 bg-white hover:bg-neutral-50 transition-colors text-sm font-medium text-neutral-900"
            title="Télécharger"
          >
            <Icon1 name="download" size={16} />
            Télécharger
          </button>
          <button
            onClick={() => setIsFullscreen((v) => !v)}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-[9px] border border-neutral-300 bg-white hover:bg-neutral-50 transition-colors text-sm font-medium text-neutral-900"
            title={isFullscreen ? "Réduire" : "Plein écran"}
          >
            <Icon1 name="full-screen" size={16} />
            {isFullscreen ? "Réduire" : "Plein écran"}
          </button>
        </div>
        <div className="flex flex-row items-center gap-3">
          {photos.length > 1 && currentIndex >= 0 && (
            <span className="text-xs text-neutral-500 tabular-nums">
              <strong className="text-neutral-900 font-semibold">
                {currentIndex + 1}
              </strong>
              <span className="text-neutral-400 mx-0.5">/</span>
              {photos.length}
            </span>
          )}
          <Button variant="small" label="Fermer" onClick={onClose} />
        </div>
      </div>
    </BaseModal>
  );
};
